import { useEffect, useState } from "react";

/**
 * "SINCE THURSDAY · ON THIS DEVICE": the last time Home was LEFT on this device,
 * per person and brand. Written when the page unmounts or the tab is hidden, so
 * a refresh does not reset it. Read once per (person, brand) visit. Storage may
 * be off (private windows); then every visit is a first one.
 */

const WEEK = 7 * 86_400_000;
const keyOf = (userId: string, brandId: string) => `co-home-left:${userId}:${brandId}`;

function read(key: string): number | null {
  try {
    const raw = window.localStorage.getItem(key);
    const value = raw ? Number(raw) : NaN;
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function write(key: string) {
  try {
    window.localStorage.setItem(key, String(Date.now()));
  } catch {
    /* storage off: nothing to remember */
  }
}

export interface LastVisit {
  since: number;
  firstVisit: boolean;
}

export function useLastVisit(userId: string | null, brandId: string | null, override?: number | null): LastVisit {
  const key = userId && brandId ? keyOf(userId, brandId) : null;
  // The visit's own read, taken when the key changes and held for the visit.
  const [visit, setVisit] = useState(() => ({ key, left: key ? read(key) : null, at: Date.now() }));
  if (visit.key !== key) setVisit({ key, left: key ? read(key) : null, at: visit.at });

  useEffect(() => {
    if (!key) return;
    const onHide = () => {
      if (document.visibilityState === "hidden") write(key);
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
      write(key);
    };
  }, [key]);

  if (override != null) return { since: override, firstVisit: false };
  return visit.left === null ? { since: visit.at - WEEK, firstVisit: true } : { since: visit.left, firstVisit: false };
}
